// Vendor's favorite organizers + a live feed of their upcoming events.
// Vendors-only. Uses /api/follows (list) and /api/follows/events (feed).
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FollowButton from "@/components/FollowButton";
import {
  Star, CalendarClock, MapPin, ArrowRight, ShieldCheck, Sparkles,
} from "lucide-react";

export default function VendorFavorites() {
  const { user, loading: authLoading } = useAuth();
  const [organizers, setOrganizers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [o, e] = await Promise.all([
        api.get("/follows"),
        api.get("/follows/events"),
      ]);
      setOrganizers(o.data.items || []);
      setEvents(e.data.items || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "vendor") load();
    // Refresh whenever any FollowButton on the app flips.
    const h = () => load();
    window.addEventListener("vendtour:follows-changed", h);
    return () => window.removeEventListener("vendtour:follows-changed", h);
  }, [user, load]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login?next=/vendor/favorites" replace />;
  if (user.role !== "vendor") return <Navigate to="/" replace />;

  return (
    <div className="mx-auto max-w-5xl px-6 py-14" data-testid="vendor-favorites-page">
      <div className="overline" style={{ color: "var(--terracotta)" }}>Vendor · Favorites</div>
      <h1 className="font-serif text-5xl mt-3">Organizers you follow.</h1>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
        Star an organizer on any event page and their new events land here
        automatically &mdash; plus you&rsquo;ll get an email + push
        notification the moment they publish.
      </p>

      {error && (
        <div className="mt-6 rounded-md p-3 text-sm"
             style={{ background: "rgba(196,79,54,0.08)", color: "var(--terracotta)" }}>
          {error}
        </div>
      )}

      <section className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <Star size={16} style={{ color: "var(--terracotta)" }} fill="currentColor" />
          <h2 className="text-lg font-medium">Favorite organizers ({organizers.length})</h2>
        </div>
        {loading ? (
          <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Loading…</div>
        ) : organizers.length === 0 ? (
          <div
            data-testid="favorites-empty-organizers"
            className="rounded-lg border p-8 text-center"
            style={{ borderColor: "rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.02)" }}
          >
            <Star size={32} className="mx-auto mb-2" style={{ color: "var(--ink-soft)" }} />
            <div className="font-serif text-2xl">No favorites yet.</div>
            <p className="mt-2 text-sm max-w-md mx-auto" style={{ color: "var(--ink-soft)" }}>
              Find an organizer you love on any event page and tap the
              &ldquo;Follow&rdquo; star &mdash; their next event will
              show up right here.
            </p>
            <Link
              to="/events"
              className="mt-6 inline-block underline text-sm"
              style={{ color: "var(--terracotta)" }}
            >
              Browse events &rarr;
            </Link>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="favorites-organizer-list"
          >
            {organizers.map((o) => (
              <div
                key={o.id}
                data-testid={`favorites-organizer-${o.id}`}
                className="rounded-lg border p-5 flex flex-wrap items-start gap-4"
                style={{ borderColor: "rgba(0,0,0,0.1)", background: "#fff" }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {o.slug ? (
                      <Link to={`/@${o.slug}`} className="font-serif text-xl hover:underline">
                        {o.business_name || o.name}
                      </Link>
                    ) : (
                      <span className="font-serif text-xl">
                        {o.business_name || o.name}
                      </span>
                    )}
                    {o.is_verified && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(132,204,22,0.14)", color: "#4B7A0A" }}
                      >
                        <ShieldCheck size={10} /> Verified
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                    {o.city && <><MapPin size={11} className="inline mr-1" />{o.city}<span className="mx-2">·</span></>}
                    <span data-testid={`favorites-upcoming-count-${o.id}`}>
                      {o.upcoming_events} upcoming
                    </span>
                  </div>
                </div>
                <FollowButton
                  organizerId={o.id}
                  organizerName={o.business_name || o.name}
                  testId={`favorites-follow-${o.id}`}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-14">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} style={{ color: "var(--avt-purple)" }} />
          <h2 className="text-lg font-medium">Upcoming events from your favorites ({events.length})</h2>
        </div>
        {loading ? (
          <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Loading…</div>
        ) : events.length === 0 ? (
          <div
            data-testid="favorites-empty-events"
            className="rounded-lg border p-8 text-center"
            style={{ borderColor: "rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.02)" }}
          >
            <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
              Nothing coming up from your favorites yet. When they post,
              you&rsquo;ll see it here first.
            </p>
          </div>
        ) : (
          <ul
            className="divide-y rounded-lg border overflow-hidden"
            style={{ borderColor: "rgba(0,0,0,0.1)", background: "#fff" }}
            data-testid="favorites-event-list"
          >
            {events.map((e) => (
              <li
                key={e.id}
                data-testid={`favorites-event-${e.id}`}
                className="px-5 py-4 flex flex-wrap sm:flex-nowrap items-center gap-4"
              >
                <div
                  className="w-10 h-10 rounded-full inline-flex items-center justify-center shrink-0"
                  style={{ background: "rgba(196,79,54,0.10)", color: "var(--terracotta)" }}
                >
                  <CalendarClock size={16} />
                </div>
                <Link
                  to={`/events/${e.id}`}
                  className="flex-1 min-w-0"
                >
                  <div className="font-medium truncate">{e.title}</div>
                  <div className="mt-0.5 text-xs" style={{ color: "var(--ink-soft)" }}>
                    <strong>{e.organizer_name}</strong>
                    {e.date && <><span className="mx-1.5">·</span>{e.date}</>}
                    {e.city && <><span className="mx-1.5">·</span><MapPin size={10} className="inline mr-0.5" />{e.city}</>}
                  </div>
                </Link>
                <Link
                  to={`/events/${e.id}`}
                  className="rounded-full p-2 hover:bg-black/5"
                  aria-label="Open event"
                >
                  <ArrowRight size={14} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
